import React, { useState, useEffect } from 'react';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import { teamService } from '../../services/api';

export default function TeamManagement() {
    const [users, setUsers] = useState<any[]>([]);
    const [usage, setUsage] = useState<any>({ current: 0, limit: 1, tier: 'Loading...' });
    const [loading, setLoading] = useState(true);
    const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'USER' });
    const [inviteLoading, setInviteLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError('');
            console.log('[TeamManagement] Fetching team data...');
            const res = await teamService.getUsers();
            console.log('[TeamManagement] API Response:', res.data);
            if (res.data.success) {
                setUsers(res.data.data.users);
                setUsage(res.data.data.usage);
            }
        } catch (err: any) {
            console.error('[TeamManagement] Error fetching team:', {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                message: err.message
            });
            // Show actual backend error message if available
            const backendMessage = err.response?.data?.message || err.response?.data?.error;
            if (err.response?.status === 401) {
                setError('Session expired. Please log in again.');
            } else if (backendMessage) {
                setError(backendMessage);
            } else {
                setError('Failed to load team data');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setInviteLoading(true);

        try {
            await teamService.inviteUser(inviteData);
            setSuccess(`Invitation sent to ${inviteData.email}`);
            setInviteData({ email: '', name: '', role: 'USER' });
            fetchUsers();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to send invitation');
        } finally {
            setInviteLoading(false);
        }
    };

    const handleRemove = async (userId: string) => {
        if (!window.confirm('Are you sure you want to remove this user?')) return;
        try {
            await teamService.removeUser(userId);
            fetchUsers();
            setSuccess('User removed successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to remove user');
        }
    };

    const isLimitReached = usage.current >= usage.limit;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
            <Navbar />

            <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            Team Management
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Manage access to your Landlord Pro account.
                        </p>
                    </div>
                    <div className="mt-4 flex md:mt-0 md:ml-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${isLimitReached ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                            {usage.tier || 'Free'} Plan: {usage.current} / {usage.limit} Users
                        </span>
                    </div>
                </div>

                {/* Invite Section */}
                <div className="bg-white shadow rounded-lg p-6 mb-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Invite New Member</h3>

                    {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>}
                    {success && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md text-sm">{success}</div>}

                    {isLimitReached ? (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <i className="fas fa-exclamation-triangle text-yellow-400"></i>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-yellow-700">
                                        You have reached the user limit for your <strong>{usage.tier}</strong> plan.
                                        Please upgrade to add more team members.
                                    </p>
                                    <a href="/settings" className="mt-2 block text-sm font-medium text-yellow-700 underline hover:text-yellow-600">
                                        Upgrade Plan &rarr;
                                    </a>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleInvite} className="grid grid-cols-1 gap-y-6 sm:grid-cols-4 sm:gap-x-4 items-end">
                            <div className="sm:col-span-1">
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    required
                                    value={inviteData.name}
                                    onChange={e => setInviteData({ ...inviteData, name: e.target.value })}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                    placeholder="John Doe"
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    required
                                    value={inviteData.email}
                                    onChange={e => setInviteData({ ...inviteData, email: e.target.value })}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                    placeholder="john@example.com"
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
                                <select
                                    id="role"
                                    name="role"
                                    value={inviteData.role}
                                    onChange={e => setInviteData({ ...inviteData, role: e.target.value })}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                >
                                    <option value="USER">User (Standard)</option>
                                    <option value="ADMIN">Admin (Full Access)</option>
                                </select>
                            </div>
                            <div className="sm:col-span-1">
                                <button
                                    type="submit"
                                    disabled={inviteLoading}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                >
                                    {inviteLoading ? 'Sending...' : 'Send Invite'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* Users List */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">Current Users</h3>
                    </div>
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Loading team...</div>
                    ) : (
                        <ul className="divide-y divide-gray-200">
                            {users.map((user) => (
                                <li key={user.sub} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0">
                                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                    <span className="text-xl font-medium text-gray-500">{user.name?.charAt(0) || user.email?.charAt(0) || 'U'}</span>
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{user.name || 'Unknown Name'}</div>
                                                <div className="text-sm text-gray-500">{user.email}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === 'CONFIRMED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {user.status}
                                            </span>
                                            <span className="ml-3 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {user.role}
                                            </span>
                                            <button
                                                onClick={() => handleRemove(user.sub)}
                                                className="ml-6 text-red-600 hover:text-red-900 text-sm font-medium"
                                                title="Remove User"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
